/* Inventory · Amazon auto-order: pot buys low items via Zinc and emails the receipt. */
import { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { ShoppingCart, Link2, Unlink, Loader2, RefreshCw, CheckCircle2, Clock, XCircle, Hourglass } from "lucide-react";

const STATUS = {
  awaiting_approval: { label: "Waiting for your OK", cls: "text-gold", Icon: Hourglass },
  placing: { label: "Placing", cls: "text-gold", Icon: Loader2 },
  processing: { label: "Amazon is confirming", cls: "text-gold", Icon: Clock },
  ordered: { label: "Ordered", cls: "text-forest", Icon: CheckCircle2 },
  failed: { label: "Didn't go through", cls: "text-clay", Icon: XCircle },
  declined: { label: "Skipped", cls: "text-moss", Icon: XCircle },
  expired: { label: "Request expired", cls: "text-moss", Icon: Clock },
};

function ConnectForm({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post("/auto-order/connect", { email, password, totp_key: totp || null }); onDone(data); }
    catch (ex) { setErr(formatErr(ex.response?.data?.detail) || ex.message); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="amazon-connect-form">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Amazon email" className="bg-cream border border-sand rounded-md px-3 py-2 text-sm" data-testid="amazon-email-input" />
      <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Amazon password" className="bg-cream border border-sand rounded-md px-3 py-2 text-sm" data-testid="amazon-password-input" />
      <input value={totp} onChange={(e) => setTotp(e.target.value)} placeholder="2-step key (optional)" className="bg-cream border border-sand rounded-md px-3 py-2 text-sm" data-testid="amazon-totp-input" />
      <div className="sm:col-span-3 flex items-center gap-3 flex-wrap">
        <button type="submit" disabled={busy} className="bg-forest text-white rounded-md px-4 py-2 text-xs font-medium hover:bg-forest-deep disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="amazon-connect-submit">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Connect Amazon
        </button>
        <span className="text-[11px] text-moss">Saved encrypted by the ordering service, used only to place your orders. Amazon charges the card saved on your Amazon account — make that your Spending card so the pot covers it.</span>
        {err && <span className="text-[11px] text-clay" data-testid="amazon-connect-error">{err}</span>}
      </div>
    </form>
  );
}

function OrderRow({ o, onDecide }) {
  const s = STATUS[o.status] || STATUS.processing;
  return (
    <li className="flex items-center justify-between gap-3 py-2 border-t border-sand/60 text-sm" data-testid={`auto-order-${o.id}`}>
      <div className="min-w-0">
        <div className="truncate"><span className="font-medium">{o.item_name}</span> ×{o.qty}{o.mock && <span className="ml-2 text-[10px] uppercase tracking-wider text-clay">mock</span>}</div>
        <div className={`text-[11px] inline-flex items-center gap-1 ${s.cls}`} data-testid={`auto-order-status-${o.id}`}>
          <s.Icon className={`w-3 h-3 ${o.status === "placing" ? "animate-spin" : ""}`} /> {s.label}
          {o.merchant_order_ids?.length > 0 && <span className="text-moss"> · #{o.merchant_order_ids.join(", ")}</span>}
          {o.error && <span className="text-moss"> · {o.error}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {o.status === "awaiting_approval" && o.approval_token && (
          <>
            <button onClick={() => onDecide(o, "approve")} className="bg-forest text-white rounded-md px-3 py-1.5 text-xs font-medium hover:bg-forest-deep" data-testid={`auto-order-approve-${o.id}`}>Yes, buy it</button>
            <button onClick={() => onDecide(o, "decline")} className="text-moss hover:text-ink text-xs px-2 py-1.5" data-testid={`auto-order-decline-${o.id}`}>Skip</button>
          </>
        )}
        <div className="font-mono text-sm tabular-nums">{o.total != null ? fmtMoney(o.total) : <span className="text-moss">~{fmtMoney(o.reserved)}</span>}</div>
      </div>
    </li>
  );
}

export default function AutoOrderCard({ onInventoryChanged }) {
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const load = useCallback(() => api.get("/auto-order").then((r) => setV(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!v?.orders?.some((o) => o.status === "processing" || o.status === "placing")) return undefined;
    const t = setInterval(() => api.post("/auto-order/refresh").then((r) => { setV(r.data); if (r.data.orders.some((o) => o.status === "ordered")) onInventoryChanged?.(); }).catch(() => {}), 30000);
    return () => clearInterval(t);
  }, [v, onInventoryChanged]);
  if (!v) return null;

  const act = async (key, fn, ok) => {
    setBusy(key); setMsg("");
    try { const r = await fn(); setV(r.data.view || r.data); if (ok) setMsg(typeof ok === "function" ? ok(r.data) : ok); }
    catch (e) { setMsg(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(""); }
  };

  return (
    <section className="mt-8 bg-linen border border-sand rounded-2xl p-6" data-testid="auto-order-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-sage"><ShoppingCart className="w-3.5 h-3.5" /> Amazon auto-order</div>
          <h2 className="font-serif text-2xl mt-1">Low item? The pot buys it.</h2>
          <p className="text-sm text-moss mt-1 max-w-xl">The moment anything drops to 25% or below, your Restock pot buys it on Amazon (exact link, or the top search result) and you get the receipt by email. With "Text me first" on, you get a text with a one-tap <em>Yes, buy it</em> before anything is ordered. If the pot is short, it waits until it isn't.</p>
          {!v.live && <p className="mt-2 text-[11px] text-clay" data-testid="auto-order-mock-notice">
            {v.service_error ? `Ordering service rejected its key (${v.service_error}) — real orders are paused until it's fixed.` : "Ordering service not connected yet — orders are simulated (nothing is really bought) until the Zinc key is added."}
          </p>}
          {v.live && v.sandbox && <p className="mt-2 text-[11px] text-gold" data-testid="auto-order-sandbox-notice">Ordering service is in test mode — orders run the full flow but nothing is really bought.</p>}
        </div>
        {v.connected && (
          <div className="flex flex-col items-end gap-2">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="auto-order-toggle">
              <input type="checkbox" checked={v.enabled} disabled={busy === "toggle"} onChange={(e) => act("toggle", () => api.post("/auto-order/settings", { enabled: e.target.checked }))} className="accent-forest w-4 h-4" />
              {v.enabled ? "Auto-order on" : "Auto-order off"}
            </label>
            <label className="inline-flex items-center gap-2 text-xs text-moss cursor-pointer" data-testid="auto-order-approval-toggle">
              <input type="checkbox" checked={v.approval_required} disabled={busy === "approval"} onChange={(e) => act("approval", () => api.post("/auto-order/settings", { approval_required: e.target.checked }))} className="accent-forest w-3.5 h-3.5" />
              Text me first — I tap "Yes, buy it"
            </label>
          </div>
        )}
      </div>

      {!v.connected ? <ConnectForm onDone={setV} /> : (
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap text-sm">
          <div className="inline-flex items-center gap-2" data-testid="amazon-connected">
            <CheckCircle2 className="w-4 h-4 text-forest" /> Connected as <span className="font-medium">{v.amazon_email}</span>
            {!v.shipping_ready && <span className="text-clay text-[11px]" data-testid="auto-order-no-shipping">· add your shipping address under Banking → Apple Pay &amp; shipping</span>}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-moss">Pot: <span className="font-mono">{fmtMoney(v.pot_balance)}</span></span>
            <button onClick={() => act("now", () => api.post("/auto-order/order-now"), (d) => { const placed = d.results.filter((r) => r.placed).length; const asking = d.results.filter((r) => r.awaiting_approval).length; const waiting = d.results.filter((r) => r.waiting).length; return placed || waiting || asking ? `${placed ? `${placed} order${placed === 1 ? "" : "s"} placed` : ""}${asking ? `${placed ? ", " : ""}${asking} waiting for your OK below` : ""}${waiting ? `${placed || asking ? ", " : ""}${waiting} waiting on the pot` : ""}.` : "Nothing low to order right now."; })}
              disabled={busy === "now" || !v.enabled} className="border border-forest text-forest rounded-md px-3 py-1.5 text-xs font-medium hover:bg-forest hover:text-white disabled:opacity-50 inline-flex items-center gap-1" data-testid="auto-order-now">
              {busy === "now" ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShoppingCart className="w-3 h-3" />} Order low items now
            </button>
            <button onClick={() => act("refresh", () => api.post("/auto-order/refresh"))} disabled={busy === "refresh"} className="text-moss hover:text-ink rounded-md px-2 py-1.5 text-xs inline-flex items-center gap-1" data-testid="auto-order-refresh">
              <RefreshCw className={`w-3 h-3 ${busy === "refresh" ? "animate-spin" : ""}`} />
            </button>
            <button onClick={() => { if (window.confirm("Disconnect Amazon? Auto-ordering stops and your login is deleted.")) act("disc", () => api.delete("/auto-order/connect"), "Amazon disconnected."); }} className="text-clay hover:bg-clay-light rounded-md px-2 py-1.5 text-xs inline-flex items-center gap-1" data-testid="amazon-disconnect">
              <Unlink className="w-3 h-3" /> Disconnect
            </button>
          </div>
        </div>
      )}

      {msg && <p className="mt-3 text-sm text-ink" data-testid="auto-order-msg">{msg}</p>}

      {v.waiting?.length > 0 && (
        <div className="mt-4 text-[12px] text-moss bg-gold/10 border border-gold/30 rounded-md px-3 py-2 inline-flex items-start gap-2" data-testid="auto-order-waiting">
          <Hourglass className="w-3.5 h-3.5 text-gold mt-0.5" />
          <span>Waiting on the pot: {v.waiting.map((w) => `${w.name} (~${fmtMoney(w.need)})`).join(", ")}. Orders go out automatically once it covers them.</span>
        </div>
      )}

      {v.orders?.length > 0 && (
        <ul className="mt-4" data-testid="auto-order-list">{v.orders.map((o) => <OrderRow key={o.id} o={o} onDecide={(x, kind) => act(kind, async () => { await api.post(`/auto-order/approval/${x.approval_token}/${kind}`); return api.get("/auto-order"); }, kind === "approve" ? `Ordering ${x.item_name} — receipt arrives by email once Amazon confirms.` : `Skipped ${x.item_name}.`)} />)}</ul>
      )}
    </section>
  );
}
